import { Controller } from "@hotwired/stimulus"
import * as pdfjsLib from "pdfjs-dist"

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs"

export default class extends Controller {
  static targets = ["canvas", "pageInfo", "prevPage", "nextPage"]
  static values  = { url: String }

  #pdfDoc      = null
  #currentPage = 1
  #totalPages  = 1
  #rendering   = false

  connect() {
    if (this.urlValue) this.#loadPdf()
  }

  disconnect() {
    this.#pdfDoc = null
  }

  async prevPage() {
    if (this.#currentPage <= 1) return
    this.#currentPage--
    await this.#renderPage(this.#currentPage)
    this.#updateControls()
  }

  async nextPage() {
    if (this.#currentPage >= this.#totalPages) return
    this.#currentPage++
    await this.#renderPage(this.#currentPage)
    this.#updateControls()
  }

  async #loadPdf() {
    try {
      this.#pdfDoc    = await pdfjsLib.getDocument(this.urlValue).promise
      this.#totalPages = this.#pdfDoc.numPages
      this.#currentPage = 1
      await this.#renderPage(this.#currentPage)
      this.#updateControls()
    } catch (err) {
      console.error("[pdf-certificate-viewer] PDF load failed:", err)
    }
  }

  async #renderPage(pageNumber) {
    if (!this.#pdfDoc || !this.hasCanvasTarget || this.#rendering) return
    this.#rendering = true

    try {
      const page   = await this.#pdfDoc.getPage(pageNumber)
      const canvas = this.canvasTarget
      const ctx    = canvas.getContext("2d")
      const dpr    = window.devicePixelRatio || 1

      // Fit the page width to the canvas container, capped for readability
      const containerWidth = canvas.parentElement?.clientWidth || 800
      const fitWidth       = Math.min(containerWidth - 32, 1200)
      const baseViewport   = page.getViewport({ scale: 1 })
      const scale          = (fitWidth / baseViewport.width) * dpr
      const viewport       = page.getViewport({ scale })

      canvas.width        = Math.round(viewport.width)
      canvas.height       = Math.round(viewport.height)
      canvas.style.width  = `${Math.round(viewport.width  / dpr)}px`
      canvas.style.height = `${Math.round(viewport.height / dpr)}px`

      ctx.fillStyle = "#ffffff"
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      await page.render({ canvasContext: ctx, viewport }).promise
    } finally {
      this.#rendering = false
    }
  }

  #updateControls() {
    if (this.hasPageInfoTarget) {
      this.pageInfoTarget.textContent = `Page ${this.#currentPage} of ${this.#totalPages}`
    }
    if (this.hasPrevPageTarget) {
      this.prevPageTarget.disabled = this.#currentPage <= 1
    }
    if (this.hasNextPageTarget) {
      this.nextPageTarget.disabled = this.#currentPage >= this.#totalPages
    }
  }
}
